/**
 * 3D Room Visualizer — Trim Mesh Builder
 *
 * Draws edge trim as simple proud strips: one box per trim piece, the width of
 * the trim's visible face, standing just in front of the board or SlatWall it
 * finishes. These are deliberately APPROXIMATE — the right size and the right
 * place, not the real cross-section. The real profiles (from CAD) will replace
 * the box with an extrusion along the same piece; everything upstream of this
 * file — which trim goes where, how long, what colour — already holds.
 *
 * Pieces arrive in surface coordinates (feet, origin bottom-left), exactly like
 * boards, so walls reuse the board face transform and the ceiling its own
 * mapping.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TrimPiece } from './types';
import { createBoardMaterial } from './materialFactory';
import { getProfile } from './profiles/profileRegistry';

/** How far the trim's face stands in front of the product it finishes (≈ 3/16"). */
const TRIM_PROUD_FT = 0.1875 / 12;

/** Depth of a trim piece from the wall plane: the product's own depth plus the proud part. */
function trimDepth(piece: TrimPiece): number {
  return getProfile(piece.productType === 'SlatWall' ? 'SlatWall' : 'WCB').depthSize + TRIM_PROUD_FT;
}

function byColor(pieces: TrimPiece[]): Map<string, TrimPiece[]> {
  const map = new Map<string, TrimPiece[]>();
  for (const p of pieces) {
    if (!map.has(p.color)) map.set(p.color, []);
    map.get(p.color)!.push(p);
  }
  return map;
}

function meshFor(geoms: THREE.BufferGeometry[], colorHex: string): THREE.Mesh | null {
  if (geoms.length === 0) return null;
  const merged = mergeGeometries(geoms, false);
  geoms.forEach((g) => g.dispose());
  if (!merged) return null;
  const material = createBoardMaterial(colorHex.toUpperCase() === '#FFFFFF' ? 'White' : 'Gray');
  const mesh = new THREE.Mesh(merged, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Trim for one wall, in canonical front-face coordinates (X along the wall, Y
 * up, Z out from the wall plane into the room). The caller applies the per-face
 * transform, as it does for boards.
 */
export function buildWallTrimGroup(pieces: TrimPiece[]): THREE.Group {
  const root = new THREE.Group();
  root.name = 'trim';
  for (const [colorHex, list] of byColor(pieces)) {
    const geoms = list
      .filter((p) => p.width > 0 && p.height > 0)
      .map((p) => {
        const depth = trimDepth(p);
        if (p.rake) {
          // Along the sloped top of the wall: a strip the length of the slope,
          // turned to it, its face hanging just below the rake line.
          const [y0, y1] = p.rake;
          const run = Math.hypot(p.width, y1 - y0);
          const angle = Math.atan2(y1 - y0, p.width);
          const g = new THREE.BoxGeometry(run, p.height, depth);
          g.translate(0, -p.height / 2, depth / 2);
          g.rotateZ(angle);
          g.translate(p.x + p.width / 2, (y0 + y1) / 2, 0);
          return g;
        }
        const g = new THREE.BoxGeometry(p.width, p.height, depth);
        g.translate(p.x + p.width / 2, p.y + p.height / 2, depth / 2);
        return g;
      });
    const mesh = meshFor(geoms, colorHex);
    if (mesh) root.add(mesh);
  }
  return root;
}

/**
 * Trim for the ceiling, in room coordinates: surface x → X, surface y → Z, hung
 * from the ceiling plane at `ceilingY` down into the room.
 */
export function buildCeilingTrimGroup(pieces: TrimPiece[], ceilingY: number): THREE.Group {
  const root = new THREE.Group();
  root.name = 'trim';
  for (const [colorHex, list] of byColor(pieces)) {
    const geoms = list
      .filter((p) => p.width > 0 && p.height > 0)
      .map((p) => {
        const depth = trimDepth(p);
        const g = new THREE.BoxGeometry(p.width, depth, p.height);
        g.translate(p.x + p.width / 2, ceilingY - depth / 2, p.y + p.height / 2);
        return g;
      });
    const mesh = meshFor(geoms, colorHex);
    if (mesh) root.add(mesh);
  }
  return root;
}
